import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import {
  clearDeviceCommand,
  commandResultQuery,
  deviceCommandsPageQuery,
  deviceCommandsTableQuery,
  resendDeviceCommand,
  type DeviceCommandCategory,
  type DeviceCommandRow,
} from '../api/deviceCmd'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { EventsChrome, type EventsFilter } from '../ui/EventsChrome'
import { Spinner } from '../ui/Spinner'

/**
 * Fila Commands: ce s-a trimis catre masina asta, si ce a raspuns.
 *
 * Portul lui `opteemum/devices/details/deviceCommands.php`. Aceeasi bara ca la
 * toate jurnalele - categorii, cautare, perioada - numai ca aici categoriile nu
 * spun felul comenzii, ci ce s-a ales de ea: Failed, Succeeded, Sent, Waiting.
 * Waiting e treapta care se cauta cel mai des: o comanda ramasa la rand
 * inseamna o masina care nu vorbeste.
 *
 * Clear si Send again nu hotarasc nimic aici: ecranul cere, serverul spune da
 * sau nu si de ce, iar motivul se scrie chiar pe randul pe care s-a apasat. Un
 * "too late" citit intr-o fereastra care se inchide singura nu e un raspuns.
 */

const TABLE = 'deviceCommands'
const EMPTY_FILTER: EventsFilter = { category: '', q: '', from: '', to: '' }

const COLUMNS: { key: 'date' | 'message' | 'category' | 'user'; title: string; titleHtml?: string; orderable: boolean; width?: number }[] = [
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', orderable: true, width: 150 },
  { key: 'message', title: 'Command', orderable: true },
  { key: 'category', title: 'State', orderable: true, width: 110 },
  { key: 'user', title: 'User', orderable: false, width: 150 },
]

const STATE_LABEL: Record<string, string> = {
  failed: 'label-danger',
  succeeded: 'label-success',
  sent: 'label-info',
  waiting: 'label-warning',
}

export function DeviceCmdCommands({ id }: { id: number }) {
  const client = useQueryClient()
  const page = useQuery(deviceCommandsPageQuery(id))
  const [filter, setFilter] = useState<EventsFilter>(EMPTY_FILTER)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'desc' })
  const [opened, setOpened] = useState<number | null>(null)
  const [said, setSaid] = useState<Record<string, string>>({})
  const [working, setWorking] = useState<string>('')

  const table = useQuery({
    ...deviceCommandsTableQuery(id, {
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search: filter.q,
      category: filter.category as DeviceCommandCategory,
      from: filter.from,
      to: filter.to,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  const result = useQuery({ ...commandResultQuery(id, opened ?? 0), enabled: opened !== null })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsFiltered ?? 0
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const at = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  const act = async (row: DeviceCommandRow, what: 'clear' | 'resend') => {
    const key = row.DT_RowId
    setWorking(key)
    try {
      const answer =
        what === 'clear'
          ? await clearDeviceCommand(id, row.origin, row.id)
          : await resendDeviceCommand(id, row.origin, row.id)
      if (answer.ok) {
        setSaid((old) => ({ ...old, [key]: '' }))
        await client.invalidateQueries({ queryKey: ['device', id, 'commands'] })
      } else {
        setSaid((old) => ({ ...old, [key]: answer.error || 'That did not work.' }))
      }
    } catch (e) {
      setSaid((old) => ({ ...old, [key]: e instanceof Error ? e.message : 'That did not work.' }))
    } finally {
      setWorking('')
    }
  }

  return (
    <div className="device-cmd-commands">
      <EventsChrome
        id="odCommandsOpteemum"
        table={`#${TABLE}`}
        categories={page.data.categories}
        startedOn={page.data.active}
        filter={filter}
        counts={table.data?.counts ?? { all: 0 }}
        onChange={(next) => {
          setFilter(next)
          setStart(0)
        }}
      />
      <div className="row">
        <div className="col-md-12">
          <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
            <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: table.isFetching ? 'block' : 'none' }}>
              Processing...
            </div>
            <table className="table table-striped table-hover dataTable commands-table no-footer" width="100%" id={TABLE} data-device={id} role="grid">
              <thead>
                <tr role="row">
                  {COLUMNS.map((col, i) => {
                    const sorted = order.column === i
                    const cls = [
                      'td-small a-left',
                      col.orderable
                        ? sorted
                          ? order.dir === 'asc'
                            ? 'sorting_asc'
                            : 'sorting_desc'
                          : 'sorting'
                        : 'sorting_disabled',
                    ].join(' ')
                    return (
                      <th
                        key={col.key}
                        className={cls}
                        style={col.width ? { width: col.width } : undefined}
                        aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                        onClick={() => {
                          if (!col.orderable) return
                          setOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                          setStart(0)
                        }}
                      >
                        {col.titleHtml ? <span dangerouslySetInnerHTML={{ __html: col.titleHtml }} /> : col.title}
                      </th>
                    )
                  })}
                  <th className="sorting_disabled td-small a-left" style={{ width: 170 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.isError ? (
                  <tr className="odd">
                    <td colSpan={5} className="dataTables_empty">
                      Something went wrong: {(table.error as Error).message}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className="odd">
                    <td colSpan={5} className="dataTables_empty">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <Fragment key={row.DT_RowId}>
                      <tr id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                        <td className="td-small a-left">{row.date}</td>
                        <td className="td-small a-left">
                          <span dangerouslySetInnerHTML={{ __html: row.message }} />
                          {row.has_result === 1 && (
                            <a
                              href="#"
                              className="ev-command"
                              data-id={row.id}
                              onClick={(e) => {
                                e.preventDefault()
                                setOpened(opened === row.id ? null : row.id)
                              }}
                            >
                              {' '}
                              <i className={`fas fa-chevron-${opened === row.id ? 'up' : 'down'}`}></i>
                            </a>
                          )}
                        </td>
                        <td className="td-small a-left">
                          <label className={`label ${STATE_LABEL[row.category] ?? 'label-default'}`}>
                            {page.data.categories[row.category] ?? row.category}
                          </label>
                        </td>
                        <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: row.user }} />
                        <td className="td-small a-left od-cmd-acts" data-id={row.id} data-origin={row.origin}>
                          <button
                            type="button"
                            className="btn btn-xs btn-default od-cmd-act"
                            data-act="clear"
                            disabled={working === row.DT_RowId}
                            onClick={() => void act(row, 'clear')}
                          >
                            {working === row.DT_RowId ? 'working...' : 'Clear'}
                          </button>{' '}
                          <button
                            type="button"
                            className="btn btn-xs btn-default od-cmd-act"
                            data-act="resend"
                            disabled={working === row.DT_RowId}
                            onClick={() => void act(row, 'resend')}
                          >
                            Send again
                          </button>
                          {said[row.DT_RowId] ? <span className="od-cmd-said">{said[row.DT_RowId]}</span> : null}
                        </td>
                      </tr>
                      {opened === row.id && (
                        <tr className="od-cmd-result">
                          <td colSpan={5}>
                            {result.isFetching ? (
                              <Spinner />
                            ) : result.data && !result.data.empty ? (
                              <pre className="od-cmd-output">{result.data.output}</pre>
                            ) : (
                              <span className="text-muted">The machine sent no output back.</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
            <div className="row">
              <div className="col-sm-2">
                <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
                  <label>
                    Show{' '}
                    <select
                      name={`${TABLE}_length`}
                      aria-controls={TABLE}
                      className="form-control input-sm"
                      value={length}
                      onChange={(e) => {
                        setLength(Number(e.target.value))
                        setStart(0)
                      }}
                    >
                      {LENGTHS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>{' '}
                    entries
                  </label>
                </div>
              </div>
              <div className="col-sm-4 aright">
                <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
                  Showing {from} to {to} of {total} entries
                </div>
              </div>
              <div className="col-sm-6">
                <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
                  <ul className="pagination">
                    <li className={`paginate_button previous${at <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          if (at > 0) setStart((at - 1) * length)
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageButtons(at, pages).map((b, i) =>
                      b === 'ellipsis' ? (
                        <li key={`e${i}`} className="paginate_button disabled">
                          <a href="#" onClick={(e) => e.preventDefault()}>
                            …
                          </a>
                        </li>
                      ) : (
                        <li key={b} className={`paginate_button${b === at ? ' active' : ''}`}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              setStart(b * length)
                            }}
                          >
                            {b + 1}
                          </a>
                        </li>
                      ),
                    )}
                    <li className={`paginate_button next${at >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          if (at < pages - 1) setStart((at + 1) * length)
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="clear"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
